import { useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { archetypes, archetypeOrder } from '../data/archetypes';
import { archetypeRelations } from '../data/synastry';
import StarField from '../components/StarField';
import RadarChart from '../components/RadarChart';
import styles from './RadarPage.module.css';

// 获取相性原因描述
function getCompatReason(primaryKey, targetKey, type, archetypes) {
  const primaryName = archetypes[primaryKey]?.name || primaryKey;
  const targetName = archetypes[targetKey]?.name || targetKey;

  if (type === '互补') {
    return `${primaryName}与${targetName}拥有互补的特质。你们各自的优势能弥补对方的盲点，在一起时能发挥出1+1>2的效果。`;
  }

  if (type === '镜像') {
    return `${primaryName}与${targetName}是镜像原型，你们在对方身上能看到自己的影子。这种相似让你们天然理解彼此，无需太多解释就能产生共鸣。`;
  }

  return '';
}

export default function RadarPage() {
  const { state, dispatch } = useApp();
  const [selectedCompat, setSelectedCompat] = useState(null);

  const { primary, primaryKey, secondary, all, compatiblePlanets } = useMemo(() => {
    const sorted = Object.entries(state.scores).sort((a, b) => b[1] - a[1]);
    const primaryKey = sorted[0] ? sorted[0][0] : 'jester';
    const primary = archetypes[primaryKey];
    const secondary = sorted[1] ? archetypes[sorted[1][0]] : null;
    const all = archetypeOrder.map((key) => ({
      key,
      ...archetypes[key],
      score: state.scores[key] || 0,
    }));

    const relations = archetypeRelations[primaryKey] || {};
    const compatiblePlanets = {
      complement: (relations.complement || []).slice(0, 2).map((key) => ({
        key,
        ...archetypes[key],
        type: '互补',
        reason: getCompatReason(primaryKey, key, '互补', archetypes),
      })),
      mirror: (relations.mirror || []).slice(0, 1).map((key) => ({
        key,
        ...archetypes[key],
        type: '镜像',
        reason: getCompatReason(primaryKey, key, '镜像', archetypes),
      })),
    };

    return { primary, primaryKey, secondary, all, compatiblePlanets };
  }, [state.scores]);

  const handleBack = () => {
    dispatch({ type: 'SET_PAGE', payload: 'result' });
  };

  return (
    <div className={styles.container}>
      <StarField density={60} speed={0.15} />

      <button className={styles.backBtn} onClick={handleBack}>
        ← 返回结果
      </button>

      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.titleDecor}>◇ ◇ ◇</div>
          <h1 className={styles.title}>原型能量图谱</h1>
          <p className={styles.subtitle}>探索你内心的十二种原始力量</p>
        </div>

        {/* 雷达图 */}
        <div className={styles.chartSection}>
          <div className={styles.chartWrapper}>
            <RadarChart data={all} />
          </div>
          <div className={styles.legend}>
            <div className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: primary.color }} />
              <span>主导: {primary.name}</span>
            </div>
            {secondary && (
              <div className={styles.legendItem}>
                <span className={styles.legendDot} style={{ background: secondary.color }} />
                <span>次要: {secondary.name}</span>
              </div>
            )}
          </div>
        </div>

        {/* 主要原型卡片 */}
        <div className={styles.primaryCard}>
          <div className={styles.primaryCardGlow} style={{ background: primary.color }} />
          <img src={primary.planetImg} alt="" className={styles.primaryPlanetImg} />
          <div className={styles.primaryInfo}>
            <div className={styles.primaryLabel}>✦ 主导原型</div>
            <div className={styles.primaryName}>{primary.name}</div>
            <div className={styles.primaryPlanet}>{primary.planet}</div>
          </div>
          <div className={styles.primaryScoreBox}>
            <div className={styles.primaryScore}>{state.scores[primaryKey] || 0}</div>
            <div className={styles.primaryScoreLabel}>能量值</div>
          </div>
        </div>

        {/* 能量分布 */}
        <div className={styles.energySection}>
          <h3 className={styles.sectionTitle}>◇ 能量分布</h3>
          <div className={styles.energyBars}>
            {all
              .sort((a, b) => b.score - a.score)
              .slice(0, 6)
              .map((arch) => (
                <div key={arch.key} className={styles.energyBar}>
                  <img src={arch.planetImg} alt="" className={styles.energyIcon} />
                  <span className={styles.energyName}>{arch.name}</span>
                  <div className={styles.energyTrack}>
                    <div
                      className={styles.energyFill}
                      style={{
                        width: `${(arch.score / Math.max(...all.map((a) => a.score))) * 100}%`,
                        background: `linear-gradient(90deg, ${arch.color}88, ${arch.color})`,
                      }}
                    />
                  </div>
                  <span className={styles.energyValue}>{arch.score}</span>
                </div>
              ))}
          </div>
        </div>

        {/* 相性星球 */}
        <div className={styles.compatSection}>
          <h3 className={styles.sectionTitle}>◇ 相性星球</h3>
          <p className={styles.compatDesc}>与你能量共振的原型</p>
          <div className={styles.compatList}>
            {[...compatiblePlanets.complement, ...compatiblePlanets.mirror].map((planet) => (
              <div
                key={planet.key}
                className={`${styles.compatItem} ${selectedCompat?.key === planet.key ? styles.compatItemActive : ''}`}
                onClick={() => setSelectedCompat(selectedCompat?.key === planet.key ? null : planet)}
              >
                <div className={styles.compatItemGlow} style={{ background: planet.color }} />
                <img src={planet.planetImg} alt="" className={styles.compatImg} />
                <div className={styles.compatInfo}>
                  <span className={styles.compatName}>{planet.name}</span>
                  <span className={styles.compatPlanet}>{planet.planet}</span>
                  <span className={styles.compatType}>{planet.type}</span>
                </div>
              </div>
            ))}
          </div>

          {selectedCompat && (
            <div className={styles.compatDetail}>
              <div className={styles.compatDetailHeader}>
                <img src={selectedCompat.planetImg} alt="" className={styles.compatDetailImg} />
                <div className={styles.compatDetailTitle}>
                  <span className={styles.compatDetailName}>{selectedCompat.name}</span>
                  <span className={styles.compatDetailType}>{selectedCompat.type}关系</span>
                </div>
                <button className={styles.compatDetailClose} onClick={() => setSelectedCompat(null)}>
                  ×
                </button>
              </div>
              <p className={styles.compatDetailReason}>{selectedCompat.reason}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
